import { createFileRoute, Link } from "@tanstack/react-router";
import { AlertTriangle, CheckCircle2, ClipboardList, PenLine, Flame, ArrowRight, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";

export const Route = createFileRoute("/refusal-victories")({
  head: () => ({
    meta: [
      { title: "214(b) F-1 Refusal Reversed: 8-Month Gap Case Study | WeLivdIt" },
      {
        name: "description",
        content:
          "How we reversed a 214(b) F-1 refusal with an 8-month employment gap and SELF funding - the exact DS-160 rewrite, funding narrative, and 20-second work pitch we used.",
      },
      { property: "og:title", content: "Case Study: Reversing a 214(b) F-1 Refusal" },
      { property: "og:image", content: "https://welivdit.com/og-share-v6.jpg" },
      { property: "og:image:width", content: "1200" },
      { property: "og:image:height", content: "630" },
      { name: "twitter:image", content: "https://welivdit.com/og-share-v6.jpg" },
      {
        property: "og:description",
        content:
          "Same university. 8-month gap. SELF funding. Approved on the second attempt. The exact WeLivdIt blueprint.",
      },
      { property: "og:type", content: "article" },
      { name: "twitter:card", content: "summary_large_image" },
      { property: "og:url", content: "https://welivdit.com/refusal-victories" },
    ],
    links: [{ rel: "canonical", href: "https://welivdit.com/refusal-victories" }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "Home", item: "https://welivdit.com/" },
            {
              "@type": "ListItem",
              position: 2,
              name: "214(b) Refusal Reversed: 8-Month Gap Case Study",
              item: "https://welivdit.com/refusal-victories",
            },
          ],
        }),
      },
    ],
  }),
  component: RefusalVictoriesPage,
});

function RefusalVictoriesPage() {
  return (
    <div className="flex flex-col bg-background text-foreground">
      <main className="flex-1">
        {/* Hero */}
        <section className="relative overflow-hidden border-b border-border/60">
          <div className="absolute inset-0 pointer-events-none bg-gradient-to-br from-primary/8 via-transparent to-amber-500/8" />
          <div className="relative mx-auto max-w-6xl px-5 py-16 sm:py-20 lg:py-24">
            <span className="inline-flex items-center gap-1.5 rounded-full border border-primary/30 bg-primary/5 px-3 py-1 text-xs font-medium text-primary">
              <Sparkles className="h-3.5 w-3.5" /> Refusal Recovery Program
            </span>
            <h1 className="mt-4 font-display text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight max-w-4xl">
              Refusal to Approval: <span className="text-primary">Our Comeback Stories</span>
            </h1>
            <p className="mt-4 text-lg text-muted-foreground max-w-2xl leading-relaxed">
              A 214(b) visa rejection is devastating, but it is not the end of your US dream. Behind every approval on
              this page was a student who got a piece of paper that said "no" - and turned it around with a sharper
              story, a cleaner DS-160, and hours of hard practice.
            </p>

            <div className="mt-6 flex items-start gap-3 rounded-2xl border border-amber-300 bg-amber-50/80 p-4 max-w-2xl dark:border-amber-500/30 dark:bg-amber-950/30">
              <AlertTriangle className="h-5 w-5 shrink-0 text-amber-600 dark:text-amber-300 mt-0.5" />
              <p className="text-sm text-amber-900 dark:text-amber-100"></p>
            </div>

            <div className="mt-8">
              <Button asChild size="lg" className="gap-2">
                <Link to="/refusal-support">
                  Book Refusal Support <ArrowRight className="h-4 w-4" />
                </Link>
              </Button>
            </div>
          </div>
        </section>

        {/* Empathy hook */}
        <section className="border-b border-border/60">
          <div className="mx-auto max-w-4xl px-5 py-16">
            <h2 className="font-display text-3xl font-bold tracking-tight">The 2-Minute Rejection: Sound Familiar?</h2>
            <ul className="mt-6 space-y-4 text-base leading-relaxed text-muted-foreground">
              <li className="flex gap-3">
                <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-primary" />
                <span>
                  You walked up to the window prepared. Two questions in, the officer stopped making eye contact and
                  started typing.
                </span>
              </li>
              <li className="flex gap-3">
                <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-primary" />
                <span>
                  Before you could explain your program, your funding, or your real intent - the blue slip was already
                  sliding under the glass.
                </span>
              </li>
              <li className="flex gap-3">
                <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-primary" />
                <span>
                  Now you're replaying every second, second-guessing every answer, and wondering if a second attempt is
                  even worth it. It is - but not with the same playbook.
                </span>
              </li>
            </ul>
          </div>
        </section>

        {/* Process */}
        <section className="border-b border-border/60">
          <div className="mx-auto max-w-6xl px-5 py-16">
            <h2 className="font-display text-3xl font-bold tracking-tight text-center">
              Our Proprietary Refusal Recovery Process
            </h2>
            <div className="mt-10 grid gap-6 md:grid-cols-3">
              {[
                {
                  n: "01",
                  icon: ClipboardList,
                  title: "The DS-160 & Profile Autopsy",
                  body: "Line-by-line audit of your previous DS-160 and profile - every inconsistency an officer could have flagged in 30 seconds is surfaced and fixed.",
                },
                {
                  n: "02",
                  icon: PenLine,
                  title: "The Narrative Rewrite",
                  body: "We rebuild your 30-second story from scratch - university choice, funding, family ties, and post-study intent - so every answer reinforces the same clean thread.",
                },
                {
                  n: "03",
                  icon: Flame,
                  title: "High-Stress Consulate Simulations",
                  body: "Aggressive back-to-back mocks that mimic hostile officer body language, rapid-fire follow-ups, and 15-second answer windows.",
                },
              ].map((s) => (
                <div key={s.n} className="rounded-3xl border border-border bg-card p-6 shadow-sm">
                  <div className="flex items-center justify-between">
                    <s.icon className="h-6 w-6 text-primary" />
                    <span className="text-xs font-mono text-muted-foreground">{s.n}</span>
                  </div>
                  <h3 className="mt-4 font-display text-lg font-semibold">{s.title}</h3>
                  <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{s.body}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Comparison */}
        <section className="border-b border-border/60 bg-muted/20">
          <div className="mx-auto max-w-5xl px-5 py-16">
            <h2 className="font-display text-3xl font-bold tracking-tight text-center">
              Standard Prep vs. Refusal Support
            </h2>
            <div className="mt-8 grid gap-5 md:grid-cols-2">
              <div className="rounded-3xl border border-border bg-background p-6">
                <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Standard Prep</p>
                <h3 className="mt-2 font-display text-xl font-semibold">First-timer Coaching</h3>
                <p className="mt-2 text-sm text-muted-foreground leading-relaxed">
                  Great for first-timers, standard mock formats, general strategy. Solid rehearsal when your profile is
                  already clean and your story is clear.
                </p>
              </div>
              <div className="relative rounded-3xl border-2 border-primary bg-primary/5 p-6 shadow-lg shadow-primary/10">
                <span className="absolute -top-3 left-6 rounded-full bg-primary px-3 py-1 text-[10px] font-bold uppercase tracking-wider text-primary-foreground">
                  For refusals
                </span>
                <p className="text-xs font-semibold uppercase tracking-wider text-primary">Refusal Support</p>
                <h3 className="mt-2 font-display text-xl font-semibold">Deep-Dive Recovery</h3>
                <p className="mt-2 text-sm text-foreground leading-relaxed">
                  Deep-dive DS-160 autopsy, complete narrative rewrite, aggressive high-stress simulation, and dedicated
                  psychological confidence rebuilding for your re-attempt.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Deep-dive Case Study */}
        <section className="border-b border-border/60">
          <div className="mx-auto max-w-4xl px-5 py-16">
            <span className="inline-flex items-center gap-1.5 rounded-full border border-primary/30 bg-primary/5 px-3 py-1 text-xs font-medium text-primary">
              <Flame className="h-3.5 w-3.5" /> Deep-Dive Case Study
            </span>
            <h2 className="mt-4 font-display text-3xl sm:text-4xl font-bold tracking-tight">
              Reversing a 214(b) F-1 Refusal with an 8-Month Employment Gap
            </h2>

            <div className="mt-8 grid gap-3 sm:grid-cols-2 rounded-3xl border border-border bg-card p-6">
              {[
                ["Applicant", "Applicant N.R."],
                ["Target Institution", "University of Florida"],
                ["Program", "M.S. Computer & Information Sciences"],
                ["Historical Track", "Refused Dec 16, 2025 (New Delhi)"],
                ["Current Status", "APPROVED · July 15, 2026 (Mumbai)"],
                ["Primary Constraints", "8-mo career gap · SELF funding · Same-school reapp"],
              ].map(([k, v]) => (
                <div key={k} className="flex flex-col">
                  <span className="text-[11px] uppercase tracking-wider text-muted-foreground">{k}</span>
                  <span className="mt-0.5 text-sm font-medium text-foreground">{v}</span>
                </div>
              ))}
            </div>

            <div className="mt-10">
              <h3 className="font-display text-xl font-semibold">Frequently Asked Questions</h3>
              <div className="mt-4 space-y-5">
                <div>
                  <p className="font-semibold text-foreground">
                    How do you justify an employment gap in a visa interview after a 214(b) refusal?
                  </p>
                  <p className="mt-1 text-sm text-muted-foreground leading-relaxed">
                    To justify a career gap after a 214(b) refusal, applicants must avoid generic explanations like
                    "exam preparation" or "resting." The optimal strategy is to anchor the narrative to prior long-term
                    corporate contributions (e.g., 4 years as a Systems Engineer at Tata Consultancy Services) and frame
                    the gap as a calculated, pre-departure transition period dedicated strictly to target program
                    technical prerequisites.
                  </p>
                </div>
                <div>
                  <p className="font-semibold text-foreground">
                    Can you get an F-1 visa approved after the same university rejected you?
                  </p>
                  <p className="mt-1 text-sm text-muted-foreground leading-relaxed">
                    Yes. Re-applying to the exact same university after an F-1 refusal requires a complete overhaul of
                    the DS-160. The applicant must explicitly address the previous breakdown in communication by
                    injecting high-density, self-aware clarity regarding their academic milestones and post-graduation
                    career trajectory directly into the official immigration record.
                  </p>
                </div>
              </div>
            </div>

            <div className="mt-10">
              <h3 className="font-display text-xl font-semibold">
                The Core Profile Dilemma: Three Structural Red Flags
              </h3>
              <ol className="mt-4 space-y-4">
                {[
                  {
                    t: "The Resignation Timeline Trap",
                    b: "The applicant resigned from her role as a Systems Engineer at Tata Consultancy Services on November 6, 2025. By her second visa attempt in July 2026, she faced a steep 8-month active unemployment gap.",
                  },
                  {
                    t: "The \u201CSELF\u201D Funding Paradox",
                    b: "Her DS-160 declared \u201CSELF\u201D as the primary source of financial support. Presenting a self-funded financial profile while carrying nearly a year of zero active income triggers intense Visa Officer (VO) scrutiny regarding fund origin and potential off-campus employment intent.",
                  },
                  {
                    t: "The Same-Institution Friction",
                    b: "Returning to the exact same university (University of Florida) without altering the fundamental choice of school inherently puts the applicant at a psychological disadvantage during re-evaluation.",
                  },
                ].map((s, i) => (
                  <li key={s.t} className="flex gap-4 rounded-2xl border border-border bg-card p-5">
                    <span className="font-mono text-xs text-muted-foreground shrink-0 pt-0.5">0{i + 1}</span>
                    <div>
                      <p className="font-semibold text-foreground">{s.t}</p>
                      <p className="mt-1 text-sm text-muted-foreground leading-relaxed">{s.b}</p>
                    </div>
                  </li>
                ))}
              </ol>
            </div>

            <div className="mt-10">
              <h3 className="font-display text-xl font-semibold">
                The WeLivdIt Blueprint: The Exact Technical Advice Given
              </h3>
              <div className="mt-4 space-y-5">
                {[
                  {
                    t: "1. The DS-160 Semantic Correction Framework",
                    p: "The initial application failed because the student's core motivation and technical timeline felt fragmented under pressure.",
                    a: "We completely re-authored the explanation of her previous refusal on the new DS-160 form. Instead of leaving it blank or sounding defensive, we drafted a transparent, expert statement: \u201CMy F-1 visa was refused on December 16, 2025, under Section 214(b). At the time, my academic plan and study objectives may not have been clearly articulated.\u201D This instantly neutralizes VO bias by signaling maturity, structural accountability, and clear intent to deliver better clarity.",
                  },
                  {
                    t: "2. Deconstructing the \u201CSELF\u201D Funding Narrative",
                    p: "Explaining how an unemployed applicant is paying for a top-tier U.S. Master's degree without raising red flags.",
                    a: "We instructed the applicant never to get defensive about the 8-month gap or volunteer unprompted financial explanations. We structurally linked her \u201CSELF\u201D funding status to her 4 consecutive years of rigorous corporate salary accumulation at a global tier-1 MNC (TCS). By proving her liquid assets were the direct, organic byproduct of her engineering career, the funding paradox was entirely resolved.",
                  },
                  {
                    t: "3. The 20-Second High-Density Work Pitch",
                    p: "Nervousness during the attempt caused the applicant to lose control of the interview flow, allowing the VO to fixate on profile weaknesses.",
                    a: "Through intense, live WeLivdIt mock testing cycles managed by senior mentors Srinath, Harsha, and Anindya, we systematically stripped away conversational fluff. We trained her to deliver a hyper-focused, 20-second distillation of her engineering tenure\u2014specifically detailing her software application maintenance, airline-based project debugging, and system performance enhancements.",
                  },
                ].map((s) => (
                  <div key={s.t} className="rounded-2xl border border-border bg-card p-5">
                    <p className="font-semibold text-foreground">{s.t}</p>
                    <div className="mt-3 space-y-3 text-sm leading-relaxed">
                      <div>
                        <p className="text-[11px] uppercase tracking-wider text-muted-foreground">The problem</p>
                        <p className="mt-0.5 text-foreground">{s.p}</p>
                      </div>
                      <div>
                        <p className="text-[11px] uppercase tracking-wider text-primary">The WeLivdIt advice</p>
                        <p className="mt-0.5 text-foreground">{s.a}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="mt-10 rounded-3xl border-2 border-emerald-500/40 bg-emerald-500/5 p-6">
              <div className="flex items-center gap-2">
                <CheckCircle2 className="h-5 w-5 text-emerald-600 dark:text-emerald-400" />
                <p className="text-xs font-semibold uppercase tracking-wider text-emerald-700 dark:text-emerald-300">
                  The Operational Outcome
                </p>
              </div>
              <p className="mt-3 text-base text-foreground leading-relaxed">
                At Counter 32 of the U.S. Consulate in Mumbai, the strategy worked with absolute precision. Confronted
                with a confident, highly articulate pitch, the Visa Officer was incredibly short, direct, and issued a
                clean F-1 visa approval.
              </p>
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="border-b border-border/60 bg-muted/20">
          <div className="mx-auto max-w-3xl px-5 py-16">
            <h2 className="font-display text-3xl font-bold tracking-tight text-center">Refusal FAQ</h2>
            <Accordion type="single" collapsible className="mt-8">
              <AccordionItem value="q1">
                <AccordionTrigger>Will my previous rejection automatically ruin my next attempt?</AccordionTrigger>
                <AccordionContent>
                  No. Officers can see your refusal history, but a refusal is not a lifetime ban. What they care about
                  is what has <em>changed</em> - your story, your DS-160, your answers, and your composure. Fix those,
                  and you walk in as a fresh case.
                </AccordionContent>
              </AccordionItem>
              <AccordionItem value="q2">
                <AccordionTrigger>Should I change my university for the second attempt?</AccordionTrigger>
                <AccordionContent>
                  Only if the change is genuine and defensible. Switching universities to "look different" without a
                  real reason is a bigger red flag than sticking with your original choice and articulating it well.
                </AccordionContent>
              </AccordionItem>
              <AccordionItem value="q3">
                <AccordionTrigger>Do I need to fill out a new DS-160?</AccordionTrigger>
                <AccordionContent>
                  Yes - every visa appointment needs a fresh DS-160 confirmation. This is a chance to fix
                  inconsistencies from your first attempt and tighten every field so it lines up perfectly with your new
                  story.
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          </div>
        </section>

        {/* Final CTA */}
        <section>
          <div className="mx-auto max-w-3xl px-5 py-20 text-center">
            <h2 className="font-display text-3xl sm:text-4xl font-bold tracking-tight">
              Don't leave your second attempt to chance.
            </h2>
            <p className="mt-4 text-muted-foreground">
              Ten students a month. First-come, first-served. When it's full, it's full.
            </p>
            <div className="mt-8">
              <Button asChild size="lg" className="gap-2 text-base">
                <Link to="/refusal-support">
                  Schedule Your Refusal Recovery Session Now <ArrowRight className="h-4 w-4" />
                </Link>
              </Button>
            </div>
          </div>
        </section>
      </main>
    </div>
  );
}
